import { Popconfirm } from 'antd';
import Badge from 'components/Badge';
import FilterMenuPopup from 'components/popup/FilterMenuPopup';
import {
  resetFilters,
  setShowDetails,
  setShowDimensions,
  setShowFilterPopup,
} from 'features/siteMapSlice';
import DetailsIcon from 'lib/assets/details.svg';
import DimensionsIcon from 'lib/assets/dimensions.svg';
import FilterIcon from 'lib/assets/filter.svg';
import ResetIcon from 'lib/assets/reset.svg';
import { useSiteMapQuery } from 'lib/hooks/queries';
import { useAppDispatch, useAppSelector } from 'lib/hooks/redux';
import {
  displayDetails,
  displayDimensions,
  hideDetails,
  hideDimensions,
  resetFilterSVG,
} from 'lib/utils';
import SVG from 'react-inlinesvg';
import styled from 'styled-components';

const MobileNav = () => {
  // Redux Hooks and States
  const dispatch = useAppDispatch();
  const { totalSelectedFilters, showFilterPopup, showDetails, showDimensions } = useAppSelector(
    (state) => ({
      totalSelectedFilters: state.siteMap.totalSelectedFilters,
      showFilterPopup: state.siteMap.showFilterPopup,
      showDetails: state.siteMap.showDetails,
      showDimensions: state.siteMap.showDimensions,
    })
  );

  // Get Site Map Data from React-Query Cache (No Params)
  const siteMapData = useSiteMapQuery().data!;

  // Semantic Vars From Site Map Data
  const {
    displayOptions: [displayOptions],
  } = siteMapData;

  // Constants
  const URLParams = new URLSearchParams(window.location.search);
  const filterPlanSysId = URLParams.get('filterPlanSysId');

  // Functions
  const handleNavClick = (clickedNav: string) => {
    switch (clickedNav) {
      case 'filter':
        dispatch(setShowFilterPopup(true));
        break;
      case 'details':
        if (showDetails) {
          hideDetails();
          dispatch(setShowDetails(false));
          if (!displayOptions.hideDimensions && showDimensions) {
            hideDimensions();
            dispatch(setShowDimensions(false));
          }
        } else {
          displayDetails();
          dispatch(setShowDetails(true));
        }
        break;
      case 'dimensions':
        if (showDimensions) {
          hideDimensions();
          dispatch(setShowDimensions(false));
        } else {
          displayDimensions();
          dispatch(setShowDimensions(true));
          if (!displayOptions.hideDetails && !showDetails) {
            displayDetails();
            dispatch(setShowDetails(true));
          }
        }
        break;
      case 'reset':
        if (showDetails) {
          hideDetails();
          dispatch(setShowDetails(false));
        }
        if (showDimensions) {
          hideDimensions();
          dispatch(setShowDimensions(false));
        }
        break;
    }
  };

  const handleReset = () => {
    resetFilterSVG(siteMapData);
    dispatch(resetFilters());
  };

  return (
    <StyledNav data-cy="nav-bar">
      <StyledNavButton
        data-cy="filter-button"
        active={showFilterPopup}
        onClick={() => handleNavClick('filter')}
      >
        {totalSelectedFilters > 0 && <Badge text={totalSelectedFilters} />}
        <SVG data-cy="filter-icon" src={FilterIcon} />
        <StyledNavButtonTitle>FILTER</StyledNavButtonTitle>
      </StyledNavButton>
      {showFilterPopup && <FilterMenuPopup navPositionX={0} />}
      {!displayOptions.hideDetails && (
        <>
          <div className="vertical-divider" />
          <StyledNavButton
            data-cy="details-button"
            active={showDetails}
            onClick={() => handleNavClick('details')}
          >
            <SVG data-cy="details-icon" src={DetailsIcon} />
            <StyledNavButtonTitle>DETAILS</StyledNavButtonTitle>
          </StyledNavButton>
        </>
      )}
      {!displayOptions.hideDimensions && (
        <>
          <div className="vertical-divider" />
          <StyledNavButton
            data-cy="dimensions-button"
            active={showDimensions}
            onClick={() => handleNavClick('dimensions')}
          >
            <SVG data-cy="dimensions-icon" src={DimensionsIcon} />
            <StyledNavButtonTitle>DIMENSIONS</StyledNavButtonTitle>
          </StyledNavButton>
        </>
      )}
      {!filterPlanSysId && (
        <>
          {totalSelectedFilters > 0 ? (
            <>
              <div className="vertical-divider" />
              <Popconfirm
                title="Are you sure you want to reset filters?"
                placement="bottomLeft"
                onConfirm={handleReset}
                okText="Yes"
                cancelText="No"
              >
                <StyledNavButton data-cy="reset-button" onClick={() => handleNavClick('reset')}>
                  <SVG data-cy="reset-icon" src={ResetIcon} />
                  <StyledNavButtonTitle>RESET</StyledNavButtonTitle>
                </StyledNavButton>
              </Popconfirm>
            </>
          ) : (
            <>
              <div className="vertical-divider" />
              <StyledNavButton data-cy="reset-button" onClick={() => handleNavClick('reset')}>
                <SVG data-cy="reset-icon" src={ResetIcon} />
                <StyledNavButtonTitle>RESET</StyledNavButtonTitle>
              </StyledNavButton>
            </>
          )}
        </>
      )}
    </StyledNav>
  );
};

export default MobileNav;

const StyledNav = styled.nav`
  display: flex;
  justify-content: space-evenly;
  background-color: ${({ theme }) => theme.colors.bgColor};
  position: absolute;
  height: 52px;
  padding-bottom: 5px;
  top: 0;
  right: 0;
  left: 0;
  z-index: 100;
  box-shadow: rgba(0, 0, 0, 0.16) 0px 1px 4px;
`;

const StyledNavButton = styled.button<{
  active?: boolean;
}>`
  padding: 0;
  margin: 0;
  border: none;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: transparent;
  overflow: hidden;
  width: 25vw;
  svg {
    height: 85%;
    width: 85%;
    color: black;
    path,
    rect,
    circle {
      fill: black;
    }
  }

  [data-cy='reset-icon'] {
    path,
    rect,
    circle {
      fill: none;
    }
  }

  ${({ active }) =>
    active &&
    `
      background-color: lightgray;
      color: black;
      width: 25vw;
    `}
`;

const StyledNavButtonTitle = styled.span`
  position: relative;
  font-size: 1.5vh;
  margin: 0;
  font-weight: bold;
  letter-spacing: 0.4px;
  @media screen and (orientation: landscape) {
    font-size: 1.3vw;
  }
`;
